import React, { useState, useEffect } from "react";
import { useWishlist } from "@/src/lib/WishlistContext";
import { useAuth } from "@/src/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Heart, ShoppingBag } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { Product } from "@/src/types";
import { ProductCard } from "@/src/components/ProductCard";

export function WishlistPage() {
  const { wishlist, loading: wishlistLoading } = useWishlist();
  const { user, isAuthReady } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthReady && !user) {
      navigate("/login");
    }
  }, [user, isAuthReady, navigate]);

  useEffect(() => {
    const fetchWishlistProducts = async () => {
      if (wishlist.length === 0) {
        setProducts([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const fetchedProducts: Product[] = [];
      for (const productId of wishlist) {
        try {
          const productDoc = await getDoc(doc(firestoreDb, "products", productId));
          if (productDoc.exists()) {
            fetchedProducts.push({ ...productDoc.data(), id: productDoc.id } as Product);
          }
        } catch (error) {
          console.error(`Error fetching product ${productId}:`, error);
        }
      }
      setProducts(fetchedProducts);
      setLoading(false);
    };

    fetchWishlistProducts();
  }, [wishlist]);

  if (wishlistLoading || loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <div className="max-w-md mx-auto">
          <Heart className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Tu lista de deseos está vacía</h2>
          <p className="text-muted-foreground mb-6">
            ¡Guarda los productos que más te gusten para verlos más tarde!
          </p>
          <Button asChild>
            <Link to="/products">Ver Productos</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Heart className="h-8 w-8 text-red-500 fill-red-500" />
        <h1 className="text-3xl font-bold">Mis Favoritos</h1>
      </div>
      
      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} />
        ))}
      </div>
    </div>
  );
}
